/**
 * Static metadata for the sample-based tanpura drone (replaces the
 * Karplus-Strong synth's live parameter derivation with a lookup against 12
 * real tanpura recordings, one per pitch class — see
 * `public/audio/tanpura/manifest.json`, the source of truth this constant is
 * copied from). Kept as a TS constant (rather than fetched at runtime) so the
 * pitch-class -> file/baseHz mapping is available synchronously and works
 * offline before the manifest.json asset itself would ever be fetched.
 */

export interface TanpuraSampleEntry {
  file: string;
  baseHz: number;
}

/** Pitch class names in semitone order starting at C, matching the manifest
 * and the `NOTE_NAMES` convention used for `resolveTanpuraSample` below. */
export const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
] as const;

export type PitchClass = (typeof NOTE_NAMES)[number];

/** Copied from `packages/web/public/audio/tanpura/manifest.json`. `baseHz` is
 * each recording's native Sa frequency (equal-tempered octave 2: C2=65.41Hz
 * ... B2=123.47Hz). `loopSec` isn't needed for playback (the AudioBufferSourceNode
 * loops the full decoded buffer), so only `file` + `baseHz` are kept here. */
export const TANPURA_SAMPLES: Record<PitchClass, TanpuraSampleEntry> = {
  C: { file: 'C.m4a', baseHz: 65.406 },
  'C#': { file: 'Csharp.m4a', baseHz: 69.296 },
  D: { file: 'D.m4a', baseHz: 73.416 },
  'D#': { file: 'Dsharp.m4a', baseHz: 77.782 },
  E: { file: 'E.m4a', baseHz: 82.407 },
  F: { file: 'F.m4a', baseHz: 87.307 },
  'F#': { file: 'Fsharp.m4a', baseHz: 92.499 },
  G: { file: 'G.m4a', baseHz: 97.999 },
  'G#': { file: 'Gsharp.m4a', baseHz: 103.826 },
  A: { file: 'A.m4a', baseHz: 110.0 },
  'A#': { file: 'Asharp.m4a', baseHz: 116.541 },
  B: { file: 'B.m4a', baseHz: 123.471 },
};

/** C0 in equal temperament (A4=440Hz standard), the reference this module's
 * nearest-semitone math is anchored to. */
const C0_HZ = 16.3516;

export interface ResolvedTanpuraSample {
  file: string;
  playbackRate: number;
}

/**
 * Pure helper: given a target Sa frequency, find the nearest pitch class (by
 * nearest equal-tempered semitone) and derive the sample-playback rate needed
 * to retune that pitch class's native recording to `saHz`.
 *
 * Octave-only differences from the recording's native octave produce an
 * exact power-of-two rate (e.g. G2->G3 is exactly 2x) — a whole-octave
 * resample has no fractional pitch-shift artifacts. A small calibration
 * detune (e.g. a Sa a few Hz off equal temperament) still resolves to the
 * nearest pitch class and yields a small continuous rate adjustment away
 * from 1.0/2.0/4.0/etc, which is the correct (and only) way to represent a
 * detuned target with a fixed recording.
 */
export function resolveTanpuraSample(saHz: number): ResolvedTanpuraSample {
  const semitonesFromC0 = Math.round(12 * Math.log2(saHz / C0_HZ));
  const pitchClassIndex = ((semitonesFromC0 % 12) + 12) % 12;
  const pitchClass = NOTE_NAMES[pitchClassIndex]!;
  const entry = TANPURA_SAMPLES[pitchClass];
  return {
    file: entry.file,
    playbackRate: saHz / entry.baseHz,
  };
}
